/**
 * layerOpacityPersistence (#625)
 *
 * Persists the user's per-layer opacity across app refresh. The live opacity is
 * otherwise held only on the in-memory layer instance and is lost when
 * refreshLayers re-initializes layers from the database default_opacity. Keys
 * are user-scoped (em3-layer-opacities:<uid>) using the same em3:bootUserId
 * convention as the enabled-flag persistence, so shared workstations don't leak
 * state between users.
 */
import { logger } from '../../../utils/logger';

const log = logger('layerOpacityPersistence');

const LAYER_OPACITY_BASE = 'em3-layer-opacities';

function userScopedKey(base: string): string | null {
  const uid = sessionStorage.getItem('em3:bootUserId');
  return uid ? `${base}:${uid}` : null;
}

/**
 * Load all persisted per-layer opacities for the current user.
 * Returns an empty map when no user is set or the stored value is malformed.
 */
export function loadPersistedLayerOpacities(): Record<string, number> {
  const key = userScopedKey(LAYER_OPACITY_BASE);
  if (!key) return {};
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    log.warn('Failed to load persisted layer opacities:', err);
  }
  return {};
}

/**
 * Persist a single layer's opacity, merging it into the current user's map of
 * opacities so previously stored layers are preserved. No-op when no user id
 * is set.
 */
export function persistLayerOpacity(layerId: string, opacity: number): void {
  const key = userScopedKey(LAYER_OPACITY_BASE);
  if (!key) return;
  try {
    const opacities = loadPersistedLayerOpacities();
    opacities[layerId] = opacity;
    localStorage.setItem(key, JSON.stringify(opacities));
  } catch (err) {
    log.warn('Failed to persist layer opacity:', err);
  }
}
